import { useCallback, useMemo } from 'react';

import type { DataFlow } from '@rtkwlf/rendall';
import { useQueryParam, withDefault } from 'use-query-params';

import { useDataFlow } from '@apis/rendall/queries/use-data-flow';
import type { GridSortModel } from '@packages/fenrir/components';
import { DataGridPro } from '@packages/fenrir/components';
import { GridEmptyOverlay } from '@packages/fenrir/components/datagrid/overlay/grid-empty-overlay/grid-empty-overlay';
import { GridErrorOverlay } from '@packages/fenrir/components/datagrid/overlay/grid-error-overlay/grid-error-overlay';
import { Stack } from '@packages/fenrir/components/stack';
import { DataGridProSortParam } from '@packages/utils/use-query-params/data-grid-pro-sort-param/data-grid-pro-sort-param';

import { useFlowDataRequest } from '../hooks/use-flow-data-request/use-flow-data-request';

import { useFlowDataColumns } from './use-flow-data-columns/use-flow-data-columns';

const emptyRows: DataFlow[] = [];
const style = { '--DataGrid-overlayHeight': '300px' };
// We don't export the MUI types from Fenrir yet, we need to export GridProSlotsComponent (preferably just all mui types)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const slotProps: any = {
  loadingOverlay: {
    variant: 'skeleton',
    noRowsVariant: 'skeleton',
  },
};
const initialState = {
  pinnedColumns: { right: ['actions'] },
};

export const FlowDataTable = () => {
  const columns = useFlowDataColumns();
  const request = useFlowDataRequest();
  const { data, isFetching, isError } = useDataFlow(request);
  const [sortModel, setSortModel] = useQueryParam(
    'sort',
    withDefault(DataGridProSortParam, [{ field: 'bytes', sort: 'desc' }]),
  );

  const rows = useMemo(
    () =>
      (data?.results ?? []).map((result, index) => ({
        id: index,
        ...result,
      })),
    [data],
  );

  const slots = useMemo(
    () => ({
      noRowsOverlay: isError
        ? () => <GridErrorOverlay />
        : () => <GridEmptyOverlay />,
    }),
    [isError],
  );

  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => setSortModel(newSortModel),
    [setSortModel],
  );

  return (
    <Stack sx={{ minHeight: 300 }}>
      <DataGridPro
        autoHeight
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        columns={columns}
        rows={isFetching ? emptyRows : rows}
        loading={isFetching}
        initialState={initialState}
        sx={style}
        sortingMode="server"
        filterMode="server"
        disableColumnMenu
        slots={slots}
        slotProps={slotProps}
        disableRowSelectionOnClick
      />
    </Stack>
  );
};
